
import React, { useState, useEffect } from 'react';
import { TransformationConfig, SavedAsset } from '../types';
import { ICONS } from '../constants';
import * as storageService from '../services/storageService';
import * as geminiService from '../services/geminiService';
import { deductCredit } from '../services/firebase';
import { POSE_LIBRARY } from '../poseData';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

interface ControlsProps {
  config: TransformationConfig;
  onChange: (newConfig: TransformationConfig) => void;
  userId?: string;
  userPlan?: 'free' | 'basic' | 'pro' | 'bronze' | 'silver' | 'gold' | 'platinum' | string;
  isAdmin?: boolean;
}

type SectionKey = 'retouching' | 'camera' | 'lighting' | 'background' | 'props' | 'pose' | 'aspect' | 'framing' | 'angle';

// Helper to convert data URL to File for Reference Image
const dataURLtoFile = (dataurl: string, filename: string): File => {
    try {
        const arr = dataurl.split(',');
        const match = arr[0].match(/:(.*?);/);
        const mime = match ? match[1] : 'image/png';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while(n--){
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, {type:mime});
    } catch (e) {
        console.error("DataURL conversion failed", e);
        return new File([], filename, { type: 'image/png' });
    }
};

interface PropThumbnailProps {
    file: File;
    index: number;
    onRemove: (idx: number) => void;
    onMagic: (f: File) => void;
    isMagicLoading: boolean;
}

// Internal Component for stable Prop Thumbnails
const PropThumbnail: React.FC<PropThumbnailProps> = ({ file, index, onRemove, onMagic, isMagicLoading }) => { 
    const [previewUrl, setPreviewUrl] = useState<string>('');

    useEffect(() => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    return (
        <div className="relative aspect-square border border-zinc-700 rounded overflow-hidden group bg-zinc-900 select-none" role="img" aria-label={`Prop thumbnail ${index + 1}`}>
             {previewUrl && (
                <img 
                    src={previewUrl} 
                    alt={`prop-${index}`} 
                    className="w-full h-full object-cover" 
                />
             )}
             
             {/* Gradient for contrast */}
             <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/40 pointer-events-none" />

             {/* Top Right: Remove Button (Always Visible & Clickable) */}
             <div className="absolute top-1 right-1 z-20">
                <button 
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(index); }}
                    className="p-1.5 bg-black/60 hover:bg-red-600 text-white rounded-full transition-colors shadow-lg backdrop-blur-sm cursor-pointer border border-white/10 focus:outline-none focus:ring-2 focus:ring-red-500"
                    title="Remove Prop"
                    aria-label="Remove Prop"
                >
                    <ICONS.Close className="w-3.5 h-3.5" />
                </button>
             </div>

             {/* Bottom Right: Magic Wand (Always Visible & Clickable) */}
             <div className="absolute bottom-1 right-1 z-20">
                <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMagic(file); }}
                    disabled={isMagicLoading}
                    className="p-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full border border-white/20 shadow-lg transition-transform hover:scale-110 active:scale-95 flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-white"
                    title="Generate Flat Lay from this image"
                    aria-label="Generate Flat Lay"
                >
                    {isMagicLoading ? <ICONS.Refresh className="w-3.5 h-3.5 animate-spin" /> : <ICONS.Wand className="w-3.5 h-3.5" />}
                </button>
             </div>
        </div>
    );
};

const Controls: React.FC<ControlsProps> = ({ config, onChange, userId, userPlan, isAdmin }) => {
  // Prop Studio State
  const [isCreatingFlatLay, setIsCreatingFlatLay] = useState(false);
  const [flatLayPreview, setFlatLayPreview] = useState<string | null>(null);

  // State for toggling sections - ALL COLLAPSED BY DEFAULT
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
      retouching: false,
      camera: false,
      lighting: false,
      background: false,
      props: false,
      pose: false,
      aspect: false,
      framing: false,
      angle: false
  });

  const toggleSection = (key: SectionKey) => {
      setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const handleFileChange = (field: 'backgroundRefImage', e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onChange({ ...config, [field]: e.target.files[0] });
    }
  };
  
  const handlePropFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
          const newFiles = Array.from(e.target.files);
          const currentFiles = config.propRefImages || [];
          onChange({ 
              ...config, 
              propRefImages: [...currentFiles, ...newFiles] 
          });
      }
  };
  
  const removePropImage = (index: number) => {
      const currentFiles = config.propRefImages || [];
      const newFiles = currentFiles.filter((_, i) => i !== index);
      onChange({ ...config, propRefImages: newFiles });
  };

  const handleCreateFlatLay = async (file: File) => {
      if (userId) {
          const hasCredit = await deductCredit(userId, 1);
          if (!hasCredit) {
              alert("Insufficient RESHOOTS! Flat Lay creation costs 1 RESHOOT.");
              return;
          }
      }

      setIsCreatingFlatLay(true);
      try {
          // PASS PLAN AND OVERRIDE TO SERVICE
          const img = await geminiService.generateFlatLayAsset(file, userPlan as string, config.outputResolution);
          setFlatLayPreview(img);
      } catch (e) {
          alert("Failed to create flat lay.");
      } finally {
          setIsCreatingFlatLay(false);
      }
  };

  const handleUseFlatLay = () => {
      if (flatLayPreview) {
          const uniqueName = `flatlay_${Date.now()}.png`;
          const file = dataURLtoFile(flatLayPreview, uniqueName);
          // Add generated flat lay to list with unique ID
          onChange({ ...config, propRefImages: [...(config.propRefImages || []), file] });
          setFlatLayPreview(null);
      }
  };

  const aspectRatios = [
      { label: '1:1', style: { width: '24px', height: '24px' } },
      { label: '3:4', style: { width: '18px', height: '24px' } },
      { label: '4:3', style: { width: '24px', height: '18px' } },
      { label: '9:16', style: { width: '14px', height: '24px' } },
      { label: '16:9', style: { width: '24px', height: '14px' } },
  ];
  
  const framingOptions = [
      { label: 'Macro', value: 'Macro: Super close view showing tiny details.', desc: 'Extreme detail focus' },
      { label: 'Closeup', value: 'Closeup: A tight view that focuses attention on the main subject.', desc: 'Tight subject focus' },
      { label: 'Medium', value: 'Medium: A standard view showing the subject clearly with some context.', desc: 'Standard balanced view' },
      { label: 'Wide', value: 'Wide: Captures the subject within a larger environment.', desc: 'Environmental context view' },
  ];

  const angleOptions = [
      { label: 'High', value: 'High Angle: Shot from above, looking down at the subject.', desc: 'Looking down view' },
      { label: 'Low', value: 'Low Angle: Shot from below, looking upward.', desc: 'Looking up view' },
      { label: 'Tilted', value: 'Tilted: The camera is angled diagonally (Dutch angle) for a dynamic look.', desc: 'Dutch angle tilt' },
      { label: 'Topdown', value: 'Topdown: Shot from directly above looking straight down (flat lay).', desc: 'Flat lay view' },
      { label: 'Straight On', value: 'Straight On: Eye level, neutral perspective.', desc: 'Eye level view' },
      { label: 'Ground', value: 'Ground Level: Extremely low angle, effectively from the floor/ground.', desc: 'Floor level view' },
      { label: 'Experimental', value: 'Experimental: Unusual, artistic, or distorted perspective.', desc: 'Artistic distorted view' }
  ];

  const cameraOptions = [
      { label: 'Pro', value: 'Pro (fujifilm x100vi)', desc: 'Sharp digital, film simulation' },
      { label: 'Film', value: 'Film (Leica M11)', desc: 'Rangefinder aesthetic, organic' },
      { label: 'Instant', value: 'Instant (Polaroid sx-70)', desc: 'Vintage, soft, chemical grain' },
      { label: 'Toy', value: 'Toy (Vivitar Ultra Wide & Slim)', desc: 'Lo-fi, vignette, distortion' }
  ];

  const lightingOptions = [
      { label: 'Ambient', value: 'Ambient', desc: 'Natural, available light' },
      { label: 'Studio Strobe', value: 'Studio Strobe', desc: 'Crisp, professional flash' },
      { label: 'Diffused Strobe', value: 'Diffused Strobe', desc: 'Soft, wrapped light' },
      { label: 'Harsh', value: 'Harsh', desc: 'High contrast, hard shadows' }
  ];
  
  const retouchingOptions = [
      { label: 'Light', value: 'Light', desc: 'Subtle fix, natural look' },
      { label: 'Moderate', value: 'Moderate', desc: 'Polished skin, younger' },
      { label: 'Extreme', value: 'Extreme', desc: 'Flawless, magazine style' }
  ];

  const SectionHeader = ({ 
      title, 
      icon: Icon, 
      sectionKey, 
      isActive 
  }: { 
      title: string; 
      icon: any; 
      sectionKey: SectionKey;
      isActive: boolean;
  }) => (
      <button 
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between group py-2 focus:outline-none focus:ring-2 focus:ring-white rounded-sm"
        aria-expanded={isActive}
        aria-controls={`${sectionKey}-panel`}
      >
        <div className="flex items-center space-x-2 text-white/90 group-hover:text-white transition-colors">
            <Icon className="w-4 h-4 text-zinc-400 group-hover:text-white transition-colors" />
            <h3 className="text-xs font-semibold uppercase tracking-widest">{title}</h3>
        </div>
        <div className="text-zinc-500 group-hover:text-white transition-colors">
            {isActive ? <ICONS.ChevronUp className="w-4 h-4" /> : <ICONS.ChevronDown className="w-4 h-4" />}
        </div>
      </button>
  );

  return (
    <div className="space-y-4 pb-20">
      
      {/* 0. ADMIN OVERRIDE SECTION */}
      {isAdmin && (
        <section className="bg-red-950/20 border border-red-900/30 rounded-lg p-3 mb-4">
             <div className="flex items-center gap-2 mb-2">
                 <ICONS.Lock className="w-3 h-3 text-red-400" />
                 <h3 className="text-[10px] font-bold uppercase tracking-wider text-red-400">Admin Override</h3>
             </div>
             <div className="grid grid-cols-3 gap-2">
                 {(['1K', '2K', '4K'] as const).map(res => {
                     const isSelected = config.outputResolution === res;
                     return (
                         <button 
                             key={res}
                             onClick={() => onChange({ 
                                 ...config, 
                                 outputResolution: isSelected ? undefined : res 
                             })}
                             className={clsx(
                                 "px-2 py-1.5 text-[10px] font-bold border rounded transition-colors",
                                 isSelected ? "bg-red-900 border-red-500 text-white" : "bg-transparent border-red-900/50 text-red-400 hover:bg-red-900/20"
                             )}
                         >
                             {res}
                         </button>
                     );
                 })}
             </div>
             {!config.outputResolution && <p className="text-[9px] text-zinc-500 mt-1.5">Using plan default.</p>}
        </section>
      )}

      {/* 1. Background Section */}
      <section className="border-b border-white/5 pb-4">
        <SectionHeader title="Background" icon={ICONS.Image} sectionKey="background" isActive={expandedSections.background} />
        
        <AnimatePresence>
            {expandedSections.background && (
                <motion.div
                    id="background-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pt-2 space-y-4"
                >
                    <div className="grid grid-cols-2 gap-2 bg-surfaceHighlight p-1 rounded-lg mb-4">
                        <button
                            onClick={() => onChange({ 
                                ...config, 
                                backgroundMode: 'keep',
                                backgroundRefImage: undefined,
                                backgroundDescription: undefined,
                                backgroundColor: undefined
                            })}
                            className={clsx(
                            "py-2 text-sm font-medium rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-white",
                            config.backgroundMode === 'keep' ? "bg-white text-black shadow-lg" : "text-zinc-400 hover:text-white"
                            )}
                        >
                            Keep
                        </button>
                        <button
                            onClick={() => onChange({ ...config, backgroundMode: 'change' })}
                            className={clsx(
                            "py-2 text-sm font-medium rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-white",
                            config.backgroundMode === 'change' ? "bg-white text-black shadow-lg" : "text-zinc-400 hover:text-white"
                            )}
                        >
                            Change
                        </button>
                    </div>

                    {config.backgroundMode === 'change' && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="space-y-4">
                                {/* Added Background Description Field */}
                                <div>
                                    <label htmlFor="bg-desc" className="block text-xs text-zinc-400 mb-2 uppercase">Background Description</label>
                                    <textarea
                                        id="bg-desc"
                                        value={config.backgroundDescription || ''}
                                        onChange={(e) => onChange({ ...config, backgroundDescription: e.target.value })}
                                        placeholder="Describe background changes..."
                                        rows={2}
                                        className="w-full bg-black/40 border border-zinc-800 rounded-md p-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-white/50 transition-colors resize-none focus:ring-1 focus:ring-white/50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-zinc-400 mb-2 uppercase">Reference Image (Optional)</label>
                                    <label className="flex-1 cursor-pointer group focus-within:ring-2 focus-within:ring-white rounded-md block">
                                            <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileChange('backgroundRefImage', e)} />
                                            <div className="h-20 w-full border border-dashed border-zinc-700 rounded-md flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                                            {config.backgroundRefImage ? (
                                                <div className="text-center">
                                                    <span className="text-xs text-white block mb-1">Selected: {config.backgroundRefImage.name}</span>
                                                    <span className="text-[10px] text-green-400">Ready to use</span>
                                                </div>
                                            ) : (
                                                <div className="text-center">
                                                    <ICONS.Image className="w-5 h-5 text-zinc-500 mx-auto mb-2"/> 
                                                    <span className="text-xs text-zinc-500">Click to Upload Reference</span>
                                                </div>
                                            )}
                                            </div>
                                    </label>
                                    {config.backgroundRefImage && (
                                        <button onClick={() => onChange({...config, backgroundRefImage: undefined})} className="mt-2 text-[10px] text-red-500 hover:text-red-400 flex items-center gap-1 focus:outline-none focus:underline">
                                            <ICONS.Trash className="w-3 h-3" /> Remove Selection
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
      </section>

      {/* 2. Props Section */}
      <section className="border-b border-white/5 pb-4">
        <SectionHeader title="Props" icon={ICONS.Briefcase} sectionKey="props" isActive={expandedSections.props} />
        
        <AnimatePresence>
            {expandedSections.props && (
                <motion.div
                    id="props-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pt-2 space-y-4"
                >
                    <div className="grid grid-cols-2 gap-2 bg-surfaceHighlight p-1 rounded-lg">
                    <button
                        onClick={() => onChange({ 
                            ...config, 
                            propMode: 'keep',
                            propRefImage: undefined,
                            propRefImages: undefined,
                            propDescription: undefined
                        })}
                        className={clsx(
                        "py-2 text-sm font-medium rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-white",
                        config.propMode === 'keep' ? "bg-white text-black shadow-lg" : "text-zinc-400 hover:text-white"
                        )}
                    >
                        Keep
                    </button>
                    <button
                        onClick={() => onChange({ ...config, propMode: 'change' })}
                        className={clsx(
                        "py-2 text-sm font-medium rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-white",
                        config.propMode === 'change' ? "bg-white text-black shadow-lg" : "text-zinc-400 hover:text-white"
                        )}
                    >
                        Change
                    </button>
                    </div>

                    {config.propMode === 'change' && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="prop-desc" className="block text-xs text-zinc-400 mb-2 uppercase">Prop Changes</label>
                                    <textarea
                                        id="prop-desc"
                                        value={config.propDescription || ''}
                                        onChange={(e) => onChange({ ...config, propDescription: e.target.value })}
                                        placeholder="Describe prop changes..."
                                        rows={2}
                                        className="w-full bg-black/40 border border-zinc-800 rounded-md p-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-white/50 transition-colors resize-none focus:ring-1 focus:ring-white/50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-zinc-400 mb-2 uppercase">Ref Images (Optional)</label>
                                    <div className="flex flex-col gap-3">
                                        <label className="w-full cursor-pointer group focus-within:ring-2 focus-within:ring-white rounded-md block">
                                                <input 
                                                    type="file" 
                                                    className="hidden" 
                                                    accept="image/*" 
                                                    multiple
                                                    onChange={handlePropFiles} 
                                                />
                                                <div className="h-16 w-full border border-dashed border-zinc-700 rounded-md flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                                                    <span className="text-xs text-zinc-500 flex items-center gap-2">
                                                        <ICONS.Image className="w-3 h-3"/> Upload Prop Refs (Multiple)
                                                    </span>
                                                </div>
                                        </label>
                                        
                                        {/* List of Uploaded Props - Refactored */}
                                        {config.propRefImages && config.propRefImages.length > 0 && (
                                            <div className="space-y-2">
                                                <div className="grid grid-cols-3 gap-2">
                                                    {config.propRefImages.map((file, idx) => (
                                                        <PropThumbnail 
                                                            key={`${file.name}-${file.size}-${idx}`}
                                                            file={file}
                                                            index={idx}
                                                            onRemove={removePropImage}
                                                            onMagic={handleCreateFlatLay}
                                                            isMagicLoading={isCreatingFlatLay}
                                                        />
                                                    ))}
                                                </div>
                                                <p className="text-[10px] text-zinc-500 italic text-right">All uploaded props will be used.</p>
                                            </div>
                                        )}
                                        
                                        {flatLayPreview && (
                                            <div className="mt-2 animate-in fade-in space-y-2 p-2 border border-white/10 rounded bg-zinc-900/50">
                                                <p className="text-[10px] text-zinc-400 uppercase tracking-wide mb-1">Generated Flat Lay</p>
                                                <div className="aspect-square w-full rounded overflow-hidden bg-white relative">
                                                    <img src={flatLayPreview} alt="Flat Lay" className="w-full h-full object-contain" />
                                                    <button 
                                                        type="button"
                                                        onClick={() => setFlatLayPreview(null)}
                                                        className="absolute top-1 right-1 bg-black/10 hover:bg-black/40 text-black rounded-full p-1"
                                                    >
                                                        <ICONS.Close className="w-3 h-3" />
                                                    </button>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button type="button" onClick={handleUseFlatLay} className="w-full py-2 bg-white text-black text-[10px] font-bold uppercase tracking-wide hover:bg-zinc-200">
                                                        Add to Selection
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
      </section>

      {/* 3. Subject Pose Section */}
      <section className="border-b border-white/5 pb-4">
        <SectionHeader title="Subject Pose" icon={ICONS.UserCheck} sectionKey="pose" isActive={expandedSections.pose} />
        
        <AnimatePresence>
            {expandedSections.pose && (
                <motion.div
                    id="pose-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pt-2 space-y-4"
                >
                    {/* Category Select */}
                    <div>
                        <label className="block text-xs text-zinc-400 mb-2 uppercase tracking-wide">Pose Category</label>
                        <select 
                            value={config.poseCategory || ''} 
                            onChange={(e) => onChange({ ...config, poseCategory: e.target.value })}
                            className="w-full bg-black/40 border border-zinc-800 rounded-md p-3 text-sm text-white focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50"
                        >
                            <option value="">Select a Category...</option>
                            {Object.keys(POSE_LIBRARY).map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                        {config.poseCategory && (
                            <p className="text-[10px] text-zinc-500 mt-1 italic">
                                Randomly selects a pose from {POSE_LIBRARY[config.poseCategory].length} variations.
                            </p>
                        )}
                    </div>

                    {/* Custom Input */}
                    <div>
                        <label className="block text-xs text-zinc-400 mb-2 uppercase tracking-wide">Custom Pose</label>
                        <textarea
                            value={config.poseDescription || ''}
                            onChange={(e) => onChange({ ...config, poseDescription: e.target.value })}
                            placeholder="Describe custom pose (overrides category)..."
                            rows={2}
                            className="w-full bg-black/40 border border-zinc-800 rounded-md p-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-white/50 resize-none focus:ring-1 focus:ring-white/50"
                        />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
      </section>

      {/* 4. Camera Type Section */}
      <section className="border-b border-white/5 pb-4">
        <SectionHeader title="Camera" icon={ICONS.Camera} sectionKey="camera" isActive={expandedSections.camera} />
        
        <AnimatePresence>
            {expandedSections.camera && (
                <motion.div
                    id="camera-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pt-2"
                >
                    <div className="grid grid-cols-2 gap-2">
                    {cameraOptions.map((cam) => {
                        const isSelected = config.cameraType === cam.value;
                        return (
                        <button
                            key={cam.label}
                            onClick={() => onChange({ ...config, cameraType: isSelected ? undefined : cam.value })}
                            className={clsx(
                                "p-3 border rounded-md transition-all flex flex-col items-start gap-1 text-left focus:outline-none focus:ring-2 focus:ring-white",
                                isSelected 
                                ? "border-white bg-white/10 text-white" 
                                : "border-zinc-800 bg-transparent text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                            )}
                        >
                            <span className="text-xs font-bold uppercase">{cam.label}</span>
                            <span className="text-[10px] text-zinc-500 leading-tight">{cam.desc}</span>
                        </button>
                        );
                    })}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
      </section>
      
      {/* 5. Lighting Section */}
      <section className="border-b border-white/5 pb-4">
        <SectionHeader title="Lighting" icon={ICONS.Zap} sectionKey="lighting" isActive={expandedSections.lighting} />
        
        <AnimatePresence>
            {expandedSections.lighting && (
                <motion.div
                    id="lighting-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pt-2"
                >
                    <div className="grid grid-cols-2 gap-2">
                    {lightingOptions.map((opt) => {
                        const isSelected = config.lighting === opt.value;
                        return (
                        <button
                            key={opt.label}
                            onClick={() => onChange({ ...config, lighting: isSelected ? undefined : opt.value })}
                            className={clsx(
                                "p-3 border rounded-md transition-all flex flex-col items-start gap-1 text-left focus:outline-none focus:ring-2 focus:ring-white",
                                isSelected 
                                ? "border-white bg-white/10 text-white" 
                                : "border-zinc-800 bg-transparent text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                            )}
                        >
                            <span className="text-xs font-bold uppercase">{opt.label}</span>
                            <span className="text-[10px] text-zinc-500 leading-tight">{opt.desc}</span>
                        </button>
                        );
                    })}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
      </section>

      {/* 6. Framing Section */}
      <section className="border-b border-white/5 pb-4">
        <SectionHeader title="Framing" icon={ICONS.Crop} sectionKey="framing" isActive={expandedSections.framing} />

        <AnimatePresence>
            {expandedSections.framing && (
                <motion.div
                    id="framing-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pt-2"
                >
                    {/* Grid of Options Only - No Toggle */}
                    <div className="grid grid-cols-2 gap-2 animate-in fade-in">
                        {framingOptions.map((opt) => {
                            const isSelected = config.framing === opt.value;
                            return (
                                <button
                                    key={opt.label}
                                    onClick={() => onChange({ 
                                        ...config, 
                                        framing: isSelected ? undefined : opt.value,
                                        framingMode: isSelected ? 'keep' : 'change' 
                                    })}
                                    className={clsx(
                                        "py-3 px-2 border rounded-md transition-all flex flex-col items-center gap-1 focus:outline-none focus:ring-2 focus:ring-white",
                                        isSelected 
                                        ? "border-white bg-white/10 text-white" 
                                        : "border-zinc-800 bg-transparent text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                                    )}
                                    title={opt.value}
                                >
                                    <span className="text-xs font-medium">{opt.label}</span>
                                    <span className="text-[10px] text-zinc-500 font-normal">{opt.desc}</span>
                                </button>
                            );
                        })}
                    </div>
                    {/* Helper text for 'Keep' state */}
                    {!config.framing && (
                        <p className="text-[10px] text-zinc-500 text-center mt-2 italic">
                            Keeping original framing. Select an option to change.
                        </p>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
      </section>

      {/* 7. Angle Section */}
      <section className="border-b border-white/5 pb-4">
        <SectionHeader title="Camera Angle" icon={ICONS.Compass} sectionKey="angle" isActive={expandedSections.angle} />

        <AnimatePresence>
            {expandedSections.angle && (
                <motion.div
                    id="angle-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pt-2"
                >
                    {/* Grid of Options Only - No Toggle */}
                    <div className="grid grid-cols-2 gap-2 animate-in fade-in">
                        {angleOptions.map((opt) => {
                            const isSelected = config.angle === opt.value;
                            return (
                                <button
                                    key={opt.label}
                                    onClick={() => onChange({ 
                                        ...config, 
                                        angle: isSelected ? undefined : opt.value,
                                        angleMode: isSelected ? 'keep' : 'change'
                                    })}
                                    className={clsx(
                                        "py-3 px-2 border rounded-md transition-all flex flex-col items-center gap-1 focus:outline-none focus:ring-2 focus:ring-white",
                                        isSelected 
                                        ? "border-white bg-white/10 text-white" 
                                        : "border-zinc-800 bg-transparent text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                                    )}
                                    title={opt.value}
                                >
                                    <span className="text-xs font-medium">{opt.label}</span>
                                    <span className="text-[10px] text-zinc-500 font-normal">{opt.desc}</span>
                                </button>
                            );
                        })}
                    </div>
                    {/* Helper text for 'Keep' state */}
                    {!config.angle && (
                        <p className="text-[10px] text-zinc-500 text-center mt-2 italic">
                            Keeping original angle. Select an option to change.
                        </p>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
      </section>

      {/* 8. Retouching Section */}
      <section className="border-b border-white/5 pb-4">
        <SectionHeader title="Retouching" icon={ICONS.ScanFace} sectionKey="retouching" isActive={expandedSections.retouching} />
        
        <AnimatePresence>
            {expandedSections.retouching && (
                <motion.div
                    id="retouching-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pt-2"
                >
                    <div className="grid grid-cols-3 gap-2">
                    {retouchingOptions.map((opt) => {
                        const isSelected = config.retouching === opt.value;
                        return (
                        <button
                            key={opt.label}
                            onClick={() => onChange({ ...config, retouching: isSelected ? undefined : opt.value as any })}
                            className={clsx(
                                "p-2 border rounded-md transition-all flex flex-col items-center gap-1 text-center focus:outline-none focus:ring-2 focus:ring-white",
                                isSelected 
                                ? "border-white bg-white/10 text-white" 
                                : "border-zinc-800 bg-transparent text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                            )}
                        >
                            <span className="text-xs font-bold uppercase">{opt.label}</span>
                            <span className="text-[9px] text-zinc-500 leading-tight hidden xl:block">{opt.desc}</span>
                        </button>
                        );
                    })}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
      </section>

      {/* 9. Aspect Ratio Section */}
      <section className="border-b border-white/5 pb-4">
        <SectionHeader title="Aspect Ratio" icon={ICONS.Monitor} sectionKey="aspect" isActive={expandedSections.aspect} />
        
        <AnimatePresence>
            {expandedSections.aspect && (
                <motion.div
                    id="aspect-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pt-2"
                >
                    <div className="grid grid-cols-5 gap-2 animate-in fade-in">
                        {aspectRatios.map((ar) => {
                            const isSelected = (config.aspectRatio || '3:4') === ar.label;
                            return (
                            <button
                                key={ar.label}
                                onClick={() => onChange({ ...config, aspectRatio: ar.label as any, aspectRatioMode: 'change' })}
                                className={clsx(
                                    "py-3 flex flex-col items-center justify-center gap-2 border rounded-md transition-all hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white",
                                    isSelected 
                                    ? "border-white bg-white/10 text-white" 
                                    : "border-zinc-800 bg-transparent text-zinc-500 hover:border-zinc-600"
                                )}
                            >
                                <div 
                                    className={clsx("border border-current rounded-sm", isSelected ? "bg-white" : "bg-transparent")} 
                                    style={ar.style}
                                />
                                <span className="text-[10px] font-mono">{ar.label}</span>
                            </button>
                            );
                        })}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
      </section>

    </div>
  );
};

export default Controls;
